import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Modal,
    Snackbar,
    Alert,
    Grid,
    CircularProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import { BASE_URL } from '../../utils/endpoints';
import Editor from 'react-simple-wysiwyg';

const fieldVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, delay: i * 0.15, ease: 'easeOut' },
    }),
};

const buttonVariants = {
    hover: { scale: 1.1, transition: { duration: 0.3 } },
    tap: { scale: 0.9 },
};

const snackbarVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const BrandEdit = ({ open, handleClose, brand, onBrandUpdated }) => {
    const initialState = {
        brand_logo: '',
        brand_name: '',
        calc_cashback: 0,
        cashback_claim_time: 0,
        cashback_rate: 0,
        category: '',
        client_link: '',
        client_offer: '',
        deals_description: '',
        default_tracking: false,
        description: '',
        estimated_cashback_date: '',
        hashtags: '',
        meta_description: '',
        meta_tag: '',
        meta_title: '',
        search_keywords: '',
        status: 'active',
        terms_conditions: '',
        tracking_speed: 0,
        trending: false,
        user_show_cashback: '',
        visit_link: '',
    };

    const [formData, setFormData] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [categories, setCategories] = useState([]);

    useEffect(() => {

        const fetchCategories = async () => {
            try {
                const response = await fetch(`${BASE_URL}/categories`);
                const data = await response.json();

                setCategories(data.categories || []);
            } catch (error) {
                console.error('DEBUG: Error fetching categories:', error);
                setSnackbarMessage('Failed to load categories');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        };
        fetchCategories();

        if (brand) {
            setFormData({
                brand_logo: brand.logo || '',
                brand_name: brand.name || '',
                calc_cashback: brand.calc_cashback || 0,
                cashback_claim_time: brand.cashback_claim_time || 0,
                cashback_rate: brand.cashbackRate || 0,
                category: brand.category || '',
                client_link: brand.client_link || '',
                client_offer: brand.offer || '',
                deals_description: brand.deals_description || '',
                default_tracking: brand.default_tracking || false,
                description: brand.description || '',
                estimated_cashback_date: brand.estimated_cashback_date || '',
                hashtags: Array.isArray(brand.hashtags) ? brand.hashtags.join(', ') : '',
                meta_description: brand.meta_description || '',
                meta_tag: Array.isArray(brand.meta_tags) ? brand.meta_tags.join(', ') : '',
                meta_title: brand.meta_title || '',
                search_keywords: Array.isArray(brand.search_keywords) ? brand.search_keywords.join(', ') : '',
                status: brand.status || 'active',
                terms_conditions: brand.terms_conditions || '',
                tracking_speed: brand.tracking_speed || 0,
                trending: brand.trending || false,
                user_show_cashback: brand.user_show_cashback || '',
                visit_link: brand.visitLink || '',
            });
        }
    }, [brand]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.brand_logo) {
            newErrors.brand_logo = 'Brand Logo URL is required';
        }
        if (!formData.brand_name) {
            newErrors.brand_name = 'Brand Name is required';
        }
        if (!formData.calc_cashback || formData.calc_cashback < 0) {
            newErrors.calc_cashback = 'Valid cashback amount is required';
        }
        if (!formData.cashback_claim_time || formData.cashback_claim_time < 0) {
            newErrors.cashback_claim_time = 'Valid claim time is required';
        }
        if (!formData.cashback_rate || formData.cashback_rate < 0) {
            newErrors.cashback_rate = 'Valid cashback rate is required';
        }
        if (!formData.category) {
            newErrors.category = 'Category is required';
        }
        if (!formData.client_link) {
            newErrors.client_link = 'Client Link is required';
        }
        if (!formData.visit_link) {
            newErrors.visit_link = 'Visit Link is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleEditorChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            description: e.target.value,
        }));
        setErrors((prev) => ({ ...prev, description: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        await handleConfirmSubmit();
    };

    const handleConfirmSubmit = async () => {
        setLoading(true);

        const payload = {
            ...formData,
            hashtags: formData.hashtags.split(',').map((tag) => tag.trim()).filter((tag) => tag),
            meta_tag: formData.meta_tag.split(',').map((tag) => tag.trim()).filter((tag) => tag),
            meta_title: formData.meta_title,
            search_keywords: formData.search_keywords.split(',').map((keyword) => keyword.trim()).filter((keyword) => keyword),
            calc_cashback: Number(formData.calc_cashback),
            cashback_claim_time: Number(formData.cashback_claim_time),
            cashback_rate: Number(formData.cashback_rate),
            tracking_speed: Number(formData.tracking_speed),
        };

        try {
            const response = await fetch(`${BASE_URL}/brands/${brand.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setSnackbarMessage('Brand updated successfully');
                setSnackbarSeverity('success');
                onBrandUpdated();
                handleClose();
            } else {
                const errorData = await response.json();
                console.error('DEBUG: API error response:', errorData);
                setSnackbarMessage(`Failed to update brand: ${errorData.message || 'Unknown error'}`);
                setSnackbarSeverity('error');
            }
        } catch (error) {
            console.error('DEBUG: Error updating brand:', error);
            setSnackbarMessage(`Error updating brand: ${error.message}`);
            setSnackbarSeverity('error');
        } finally {
            setSnackbarOpen(true);
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFormData(initialState);
        setErrors({});
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    return (
        <>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="edit-brand-modal-title"
                aria-describedby="edit-brand-modal-description"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 600, md: 700 },
                            maxHeight: '85vh',
                            overflowY: 'auto',
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: { xs: 2, sm: 4 },
                            borderRadius: 2,
                            outline: 'none',
                        }}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Typography
                                id="edit-brand-modal-title"
                                variant="h5"
                                component="h2"
                                sx={{ color: '#2e7d32', mb: 2, fontWeight: 'bold' }}
                            >
                                Edit Brand
                            </Typography>
                        </motion.div>
                        <form onSubmit={handleSubmit}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {/* Brand Identity */}
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 600,
                                            color: '#2e7d32',
                                        }}
                                    >
                                        Brand Identity
                                    </Typography>
                                    <motion.div
                                        initial="hidden"
                                        animate="visible"
                                        variants={fieldVariants}
                                        sx={{ display: 'flex', flexDirection: 'column', gap: '2' }}
                                    >
                                        {formData.brand_logo && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                                <img
                                                    src={formData.brand_logo}
                                                    alt="Brand Logo Preview"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '100%',
                                                        objectFit: 'contain',
                                                        borderRadius: '4px',
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Box sx={{ mb: 2 }}>
                                            <TextField
                                                label="Brand Logo URL"
                                                name="brand_logo"
                                                type="url"
                                                value={formData.brand_logo}
                                                onChange={handleChange}
                                                fullWidth
                                                size="small"
                                                required
                                                error={!!errors.brand_logo}
                                                helperText={errors.brand_logo}
                                                variant="outlined"
                                                disabled={loading}
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <Box>
                                            <TextField
                                                label="Brand Name"
                                                name="brand_name"
                                                type="text"
                                                value={formData.brand_name}
                                                onChange={handleChange}
                                                required
                                                fullWidth
                                                size="small"
                                                error={!!errors.brand_name}
                                                helperText={errors.brand_name}
                                                variant="outlined"
                                                disabled={loading}
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </motion.div>
                                </Box>

                                {/* Cashback Details */}
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 600,
                                            color: '#2e7d32',
                                        }}
                                    >
                                        Cashback Details
                                    </Typography>
                                    <motion.div
                                        custom={1}
                                        initial="hidden"
                                        animate="visible"
                                        variants={fieldVariants}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                            <TextField
                                                label="Calculated Cashback"
                                                name="calc_cashback"
                                                type="number"
                                                value={formData.calc_cashback}
                                                onChange={handleChange}
                                                size="small"
                                                error={!!errors.calc_cashback}
                                                helperText={errors.calc_cashback}
                                                variant="outlined"
                                                disabled={loading}
                                                required
                                                fullWidth
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            />
                                            <TextField
                                                label="Cashback Claim Time (days)"
                                                name="cashback_claim_time"
                                                type="number"
                                                value={formData.cashback_claim_time}
                                                onChange={handleChange}
                                                size="small"
                                                error={!!errors.cashback_claim_time}
                                                helperText={errors.cashback_claim_time}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                disabled={loading}
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            />
                                            <TextField
                                                label="Cashback Rate (%)"
                                                name="cashback_rate"
                                                type="number"
                                                value={formData.cashback_rate}
                                                onChange={handleChange}
                                                fullWidth
                                                size="small"
                                                required
                                                error={!!errors.cashback_rate}
                                                helperText={errors.cashback_rate}
                                                variant="outlined"
                                                disabled={loading}
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </motion.div>
                                </Box>

                                {/* Links and Category */}
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 600,
                                            color: '#2e7d32',
                                        }}
                                    >
                                        Links and Category
                                    </Typography>
                                    <motion.div
                                        custom={1}
                                        initial="hidden"
                                        animate="visible"
                                        variants={fieldVariants}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                            <TextField
                                                label="Client Link"
                                                name="client_link"
                                                type="url"
                                                value={formData.client_link}
                                                onChange={handleChange}
                                                size="small"
                                                required
                                                error={!!errors.client_link}
                                                helperText={errors.client_link}
                                                variant="outlined"
                                                disabled={loading}
                                                fullWidth
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            />
                                            <TextField
                                                label="Client Offer"
                                                name="client_offer"
                                                type="text"
                                                value={formData.client_offer}
                                                onChange={handleChange}
                                                fullWidth
                                                size="small"
                                                required
                                                variant="outlined"
                                                disabled={loading}
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            />
                                            <TextField
                                                select
                                                label="Category"
                                                name="category"
                                                value={formData.category}
                                                onChange={handleChange}
                                                fullWidth
                                                size="small"
                                                required
                                                error={!!errors.category}
                                                helperText={errors.category}
                                                variant="outlined"
                                                disabled={loading}
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            >
                                                {categories.map((category) => (
                                                    <MenuItem key={category.id} value={category.name}>
                                                        {category.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <TextField
                                                label="Visit Link"
                                                name="visit_link"
                                                type="url"
                                                value={formData.visit_link}
                                                onChange={handleChange}
                                                size="small"
                                                error={!!errors.visit_link}
                                                helperText={errors.visit_link}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                disabled={loading}
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </motion.div>
                                </Box>

                                {/* Status and Tracking */}
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 600,
                                            color: '#2e7d32',
                                        }}
                                    >
                                        Status and Tracking
                                    </Typography>
                                    <motion.div
                                        custom={3}
                                        initial="hidden"
                                        animate="visible"
                                        variants={fieldVariants}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                            <Box>
                                                <TextField
                                                    select
                                                    label="Status"
                                                    name="status"
                                                    value={formData.status}
                                                    onChange={handleChange}
                                                    InputLabelProps={{
                                                        style: { color: '#2e7d32' },
                                                        sx: {
                                                            '&.Mui-focused': {
                                                                color: '#2e7d32',
                                                            },
                                                        },
                                                    }}
                                                    fullWidth
                                                    size="small"
                                                    required
                                                    variant="outlined"
                                                    disabled={loading}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': { borderColor: '#2e7d32' },
                                                            '&:hover fieldset': { borderColor: '#2e7d32' },
                                                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value="active">Active</MenuItem>
                                                    <MenuItem value="inactive">Inactive</MenuItem>
                                                </TextField>
                                                <TextField
                                                    label="Estimated Cashback Date"
                                                    name="estimated_cashback_date"
                                                    type="date"
                                                    value={formData.estimated_cashback_date}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    InputLabelProps={{
                                                        style: { color: '#2e7d32' },
                                                        sx: {
                                                            '&.Mui-focused': {
                                                                color: '#2e7d32',
                                                            },
                                                        },
                                                    }}
                                                    size="small"
                                                    required
                                                    variant="outlined"
                                                    disabled={loading}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': { borderColor: '#2e7d32' },
                                                            '&:hover fieldset': { borderColor: '#2e7d32' },
                                                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                        },
                                                        mt: 2,
                                                    }}
                                                />
                                            </Box>
                                            <Box>
                                                <TextField
                                                    label="User Show Cashback"
                                                    name="user_show_cashback"
                                                    value={formData.user_show_cashback}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    size="small"
                                                    InputLabelProps={{
                                                        style: { color: '#2e7d32' },
                                                        sx: {
                                                            '&.Mui-focused': {
                                                                color: '#2e7d32',
                                                            },
                                                        },
                                                    }}
                                                    required
                                                    variant="outlined"
                                                    disabled={loading}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': { borderColor: '#2e7d32' },
                                                            '&:hover fieldset': { borderColor: '#2e7d32' },
                                                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                        },
                                                    }}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            name="default_tracking"
                                                            checked={formData.default_tracking}
                                                            onChange={handleChange}
                                                            sx={{
                                                                color: '#2e7d32',
                                                                '&.Mui-checked': { color: '#2e7d32' },
                                                            }}
                                                        />
                                                    }
                                                    label="Default Tracking"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            name="trending"
                                                            checked={formData.trending}
                                                            onChange={handleChange}
                                                            sx={{
                                                                color: '#2e7d32',
                                                                '&.Mui-checked': { color: '#2e7d32' },
                                                            }}
                                                        />
                                                    }
                                                    label="Trending"
                                                />
                                            </Box>
                                        </Box>
                                    </motion.div>
                                </Box>

                                {/* SEO and Metadata */}
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 600,
                                            color: '#2e7d32',
                                        }}
                                    >
                                        SEO and Metadata
                                    </Typography>
                                    <motion.div
                                        custom={4}
                                        initial="hidden"
                                        animate="visible"
                                        variants={fieldVariants}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box>
                                                <TextField
                                                    label="Meta Title"
                                                    name="meta_title"
                                                    value={formData.meta_title}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    size="small"
                                                    InputLabelProps={{
                                                        style: { color: '#2e7d32' },
                                                        sx: {
                                                            '&.Mui-focused': {
                                                                color: '#2e7d32',
                                                            },
                                                        },
                                                    }}
                                                    required
                                                    variant="outlined"
                                                    disabled={loading}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': { borderColor: '#2e7d32' },
                                                            '&:hover fieldset': { borderColor: '#2e7d32' },
                                                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                        },
                                                    }}
                                                />
                                            </Box>
                                            <Box>
                                                <TextField
                                                    label="Meta Tags (comma-separated)"
                                                    name="meta_tag"
                                                    value={formData.meta_tag}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    size="small"
                                                    InputLabelProps={{
                                                        style: { color: '#2e7d32' },
                                                        sx: {
                                                            '&.Mui-focused': {
                                                                color: '#2e7d32',
                                                            },
                                                        },
                                                    }}
                                                    variant="outlined"
                                                    disabled={loading}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': { borderColor: '#2e7d32' },
                                                            '&:hover fieldset': { borderColor: '#2e7d32' },
                                                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                        },
                                                    }}
                                                />
                                                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {formData.meta_tag
                                                        .split(',')
                                                        .map((item) => item.trim())
                                                        .filter(Boolean)
                                                        .map((keyword, idx) => (
                                                            <Box
                                                                key={idx}
                                                                sx={{
                                                                    px: 1,
                                                                    py: 0.2,
                                                                    bgcolor: '#e8f5e9',
                                                                    color: '#2e7d32',
                                                                    borderRadius: 1,
                                                                    fontSize: 13,
                                                                }}
                                                            >
                                                                {keyword}
                                                            </Box>
                                                        ))}
                                                </Box>
                                            </Box>
                                            <Box>
                                                <TextField
                                                    label="Hashtags (comma-separated)"
                                                    name="hashtags"
                                                    value={formData.hashtags}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    size="small"
                                                    InputLabelProps={{
                                                        style: { color: '#2e7d32' },
                                                        sx: {
                                                            '&.Mui-focused': {
                                                                color: '#2e7d32',
                                                            },
                                                        },
                                                    }}
                                                    variant="outlined"
                                                    disabled={loading}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': { borderColor: '#2e7d32' },
                                                            '&:hover fieldset': { borderColor: '#2e7d32' },
                                                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                        },
                                                    }}
                                                />
                                                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {formData.hashtags
                                                        .split(',')
                                                        .map((item) => item.trim())
                                                        .filter(Boolean)
                                                        .map((keyword, idx) => (
                                                            <Box
                                                                key={idx}
                                                                sx={{
                                                                    px: 1,
                                                                    py: 0.2,
                                                                    bgcolor: '#e8f5e9',
                                                                    color: '#2e7d32',
                                                                    borderRadius: 1,
                                                                    fontSize: 13,
                                                                }}
                                                            >
                                                                {keyword}
                                                            </Box>
                                                        ))}
                                                </Box>
                                            </Box>
                                            <Box>
                                                <TextField
                                                    label="Search Keywords (comma-separated)"
                                                    name="search_keywords"
                                                    value={formData.search_keywords}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    size="small"
                                                    InputLabelProps={{
                                                        style: { color: '#2e7d32' },
                                                        sx: {
                                                            '&.Mui-focused': {
                                                                color: '#2e7d32',
                                                            },
                                                        },
                                                    }}
                                                    variant="outlined"
                                                    disabled={loading}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': { borderColor: '#2e7d32' },
                                                            '&:hover fieldset': { borderColor: '#2e7d32' },
                                                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                        },
                                                    }}
                                                />
                                                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {formData.search_keywords
                                                        .split(',')
                                                        .map((item) => item.trim())
                                                        .filter(Boolean)
                                                        .map((keyword, idx) => (
                                                            <Box
                                                                key={idx}
                                                                sx={{
                                                                    px: 1,
                                                                    py: 0.2,
                                                                    bgcolor: '#e8f5e9',
                                                                    color: '#2e7d32',
                                                                    borderRadius: 1,
                                                                    fontSize: 13,
                                                                }}
                                                            >
                                                                {keyword}
                                                            </Box>
                                                        ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </motion.div>
                                </Box>

                                {/* Descriptions */}
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 600,
                                            color: '#2e7d32',
                                        }}
                                    >
                                        Descriptions
                                    </Typography>
                                    <motion.div
                                        custom={5}
                                        initial="hidden"
                                        animate="visible"
                                        variants={fieldVariants}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{ mb: 1, color: '#2e7d32' }}
                                                >
                                                    Description
                                                </Typography>
                                                <Editor
                                                    value={formData.description}
                                                    onChange={handleEditorChange}
                                                    containerProps={{
                                                        style: {
                                                            border: '1px solid #2e7d32',
                                                            borderRadius: '4px',
                                                            minHeight: '100px',
                                                            padding: '8px',
                                                            backgroundColor: '#fff',
                                                            resize: 'vertical',
                                                        },
                                                    }}
                                                    disabled={loading}
                                                />
                                            </Box>
                                            <TextField
                                                label="Deals Description"
                                                name="deals_description"
                                                value={formData.deals_description}
                                                onChange={handleChange}
                                                fullWidth
                                                size="small"
                                                multiline
                                                rows={3}
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                variant="outlined"
                                                disabled={loading}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            />
                                            <TextField
                                                label="Meta Description"
                                                name="meta_description"
                                                value={formData.meta_description}
                                                onChange={handleChange}
                                                fullWidth
                                                size="small"
                                                multiline
                                                rows={3}
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                variant="outlined"
                                                disabled={loading}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            />
                                            <TextField
                                                label="Terms & Conditions"
                                                name="terms_conditions"
                                                value={formData.terms_conditions}
                                                onChange={handleChange}
                                                fullWidth
                                                size="small"
                                                multiline
                                                rows={3}
                                                InputLabelProps={{
                                                    style: { color: '#2e7d32' },
                                                    sx: {
                                                        '&.Mui-focused': {
                                                            color: '#2e7d32',
                                                        },
                                                    },
                                                }}
                                                variant="outlined"
                                                disabled={loading}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: '#2e7d32' },
                                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </motion.div>
                                </Box>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.8 }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
                                        <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
                                            <Button
                                                variant="outlined"
                                                onClick={handleClose}
                                                fullWidth
                                                sx={{
                                                    color: '#757575',
                                                    borderColor: '#757575',
                                                    '&:hover': { borderColor: '#616161', backgroundColor: '#f5f5f5' },
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </motion.div>
                                        <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
                                            <Button
                                                variant="outlined"
                                                onClick={handleReset}
                                                fullWidth
                                                sx={{
                                                    color: '#2e7d32',
                                                    borderColor: '#2e7d32',
                                                    '&:hover': { borderColor: '#2e7d32', backgroundColor: '#e3f2fd' },
                                                }}
                                            >
                                                Reset
                                            </Button>
                                        </motion.div>
                                        <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap">
                                            <Button
                                                variant="contained"
                                                type="submit"
                                                disabled={loading}
                                                fullWidth
                                                sx={{
                                                    backgroundColor: '#2e7d32',
                                                    '&:hover': { backgroundColor: '#1b5e20' },
                                                }}
                                            >
                                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Brand'}
                                            </Button>
                                        </motion.div>
                                    </Box>
                                </motion.div>
                            </Box>
                        </form>
                    </Box>
                </motion.div>
            </Modal>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <motion.div
                    variants={snackbarVariants}
                    initial="hidden"
                    animate={snackbarOpen ? 'visible' : 'hidden'}
                >
                    <Alert
                        onClose={handleSnackbarClose}
                        severity={snackbarSeverity}
                        sx={{ width: '100%', bgcolor: snackbarSeverity === 'success' ? '#e8f5e9' : '#ffebee' }}
                    >
                        {snackbarMessage}
                    </Alert>
                </motion.div>
            </Snackbar>
        </>
    );
};

export default BrandEdit;